import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Modal,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Grid,
  Autocomplete,
  Switch,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment-timezone';
import { message } from 'antd';

// Constants
const DEFAULT_MESSAGE = `Hi {CandidateName},\nWe tried calling you about {JobTitle}.\nYou can contact {RecruiterEmail} for more details. \n\n- {RecruiterName}`;
const VALID_VARIABLES = {
  '{CandidateName}': ['candidatename', 'candidate_name', 'candidate'],
  '{JobTitle}': ['jobtitle', 'job_title', 'job'],
  '{RecruiterName}': ['recruitername', 'recruiter_name', 'recruiter'],
  '{RecruiterEmail}': ['recruiteremail', 'recruiter_email', 'email']
};
const QUESTION_MAPPING = {
  1: "Candidate's current salary including superannuation in AUD.",
  2: "Candidate's salary expectations for the job including superannuation in AUD.",
  3: "Candidate's work rights in Australia.",
  4: "Candidate's notice period in days.",
  7: "Is the candidate currently interviewing with other companies.",
  8: "If the candidate will accept fully remote work arrangements.",
  9: "If the candidate will accept hybrid work arrangements.",
  10: "If the candidate will accept fully in-person work arrangements.",
  11: "What is your location, city & suburb?",
  12: "Do you have your own transport?",
  13: "What is your hourly rate?",
  14: "What is your daily rate?",
  15: "What day are you available to start?",
  16: "What is your security clearance level?",
};
const MAX_QUESTIONS = 4;
const MAX_MESSAGE_LENGTH = 250;

function PreScreeningModal({ open, closeModal, record, backendURL, getCookie, jobTitle, jobID, onPrescreenSubmitted }) {
  // Determine if we're handling multiple candidates
  const isMultipleCandidate = Array.isArray(record);

  // State for API operations
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    // Shared form fields
    shared: {
      jobTitle: '',
      jobID: '',
      contactMethod: '',
      selectedQuestions: [],
      contactTimePreference: '',
      retryCount: '',
      scheduleInterview: false,
      sendEmail: false,
      interviewSlot: '',
      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      sendMissedCallMessage: false,
      missedCallMessage: '',
    },
    // Candidate-specific data
    candidates: [],
    // Track statuses
    candidateStatuses: {},
  });

  // Form validation state
  const [formErrors, setFormErrors] = useState({
    shared: {},
    candidates: {},
    message: null
  });

  // UI state
  const [expandedPanel, setExpandedPanel] = useState(false);
  const [expandedMainPanel, setExpandedMainPanel] = useState(false);

  // Candidate classification state
  const [pendingCandidates, setPendingCandidates] = useState([]);
  const [completedCandidates, setCompletedCandidates] = useState([]);
  const [invalidCandidates, setInvalidCandidates] = useState([]);

  // Create initial candidate state from record
  const createInitialCandidateState = useCallback((candidateData) => ({
    candidateName: candidateData ? candidateData.Name : '',
    candidateNumber: candidateData ? candidateData.Phone : '',
    candidateEmail: candidateData ? candidateData.Email : '',
    applicantID: candidateData ? candidateData.ApplicationID : '',
    errors: {}
  }), []);

  // Reset all state when modal closes
  const resetState = useCallback(() => {
    setFormData({
      shared: {
        jobTitle: '',
        jobID: '',
        contactMethod: '',
        selectedQuestions: [],
        contactTimePreference: '',
        retryCount: '',
        scheduleInterview: false,
        sendEmail: false,
        interviewSlot: '',
        userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        sendMissedCallMessage: false,
        missedCallMessage: '',
      },
      candidates: [],
      candidateStatuses: {},
    });
    setFormErrors({
      shared: {},
      candidates: {},
      message: null
    });
    setPendingCandidates([]);
    setCompletedCandidates([]);
    setInvalidCandidates([]);
    setApiError(null);
    setIsLoading(false);
  }, []);

  // Handle modal close
  const handleClose = useCallback(() => {
    resetState();
    closeModal();
  }, [closeModal, resetState]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (open) {
      setIsLoading(true);

      // Set basic form data
      setFormData(prev => ({
        ...prev,
        shared: {
          ...prev.shared,
          jobTitle: jobTitle || '',
          jobID: jobID || '',
          cookie: getCookie("userSession"),
        },
        candidates: isMultipleCandidate
            ? record.map(candidate => createInitialCandidateState(candidate))
            : record ? [createInitialCandidateState(record)] : [],
      }));

      // Check prescreening status for each candidate if multiple candidates
      if (open && isMultipleCandidate && record?.length > 0) {
        Promise.all(
            record.map(candidate =>
                axios.get(
                    `${backendURL}/v1/prescreen-result?sessionID=${getCookie("userSession")}&applicationID=${candidate.ApplicationID}`
                )
                    .then(response => ({
                      candidate,
                      response: response.data,
                      error: null
                    }))
                    .catch(error => ({
                      candidate,
                      response: null,
                      error
                    }))
            )
        )
            .then(results => {
              // Extract statuses and categorize candidates
              const statuses = {};
              const pending = [];
              const completed = [];

              results.forEach(({ candidate, response, error }) => {
                if (!error && response) {
                  statuses[candidate.ApplicationID] = response.status;

                  if (response.status === "pending") {
                    pending.push(candidate);
                  } else if (response.status === "ready") {
                    completed.push(candidate);
                  }
                }
              });

              // Update state with results
              setFormData(prev => ({
                ...prev,
                candidateStatuses: statuses
              }));
              setPendingCandidates(pending);
              setCompletedCandidates(completed);
            })
            .catch(error => {
              setApiError("Failed to check candidate statuses: " + error.message);
            })
            .finally(() => {
              setIsLoading(false);
            });
      } else {
        setIsLoading(false);
      }
    }
  }, [open, record, jobTitle, jobID, backendURL, getCookie, isMultipleCandidate, createInitialCandidateState]);

  // Validate individual candidate
  const validateCandidate = useCallback((candidate) => {
    const errors = {};

    // Name validation
    if (!candidate.candidateName || candidate.candidateName.trim() === '' || candidate.candidateName === '-') {
      errors.candidateName = 'Candidate name is required';
    }

    // Phone validation with country code check
    if (!candidate.candidateNumber || candidate.candidateNumber.trim() === '' || candidate.candidateNumber === '-') {
      errors.candidateNumber = 'Phone number is required';
    } else if (!/^(\+|00)/.test(candidate.candidateNumber)) {
      errors.candidateNumber = 'Must include country code (e.g., +61)';
    }

    // Email validation
    if (!candidate.candidateEmail || candidate.candidateEmail.trim() === '' || candidate.candidateEmail === '-') {
      errors.candidateEmail = 'Email is required';
    }

    return errors;
  }, []);

  // Validate entire form
  const validateForm = useCallback(() => {
    const errors = {
      shared: {},
      candidates: {},
      message: null
    };

    // Validate shared fields
    if (!formData.shared.contactMethod) {
      errors.shared.contactMethod = 'Contact method is required';
    }

    if (formData.shared.selectedQuestions.length === 0) {
      errors.shared.selectedQuestions = 'At least one question must be selected';
    }

    if (!formData.shared.contactTimePreference) {
      errors.shared.contactTimePreference = 'Preferred contact time is required';
    }

    if (!formData.shared.userTimeZone) {
      errors.shared.userTimeZone = 'Time zone is required';
    }

    // Validate retry count (must be between 0 and 5)
    if (formData.shared.retryCount === '') {
      errors.shared.retryCount = 'Retry count is required';
    } else {
      const retryCount = parseInt(formData.shared.retryCount, 10);
      if (isNaN(retryCount)) {
        errors.shared.retryCount = 'Retry count must be a number';
      } else if (retryCount < 0) {
        errors.shared.retryCount = 'Retry count cannot be negative';
      } else if (retryCount > 5) {
        errors.shared.retryCount = 'Retry count cannot exceed 5';
      }
    }

    if (formData.shared.scheduleInterview && !formData.shared.interviewSlot) {
      errors.shared.interviewSlot = 'Interview duration is required';
    }

    // Validate SMS message if enabled
    if (formData.shared.sendMissedCallMessage) {
      const message = formData.shared.missedCallMessage;

      if (!message.trim()) {
        errors.message = 'Missed call message is required when message sending is enabled';
      } else if (message.length > MAX_MESSAGE_LENGTH) {
        errors.message = `Message must not exceed ${MAX_MESSAGE_LENGTH} characters`;
      } else {
        // Check for invalid variables
        const invalidVars = findInvalidVariables(message);
        if (invalidVars.length > 0) {
          errors.message = 'Invalid variable format found - see suggestions below';
        }
      }
    }

    // Validate each candidate
    const invalidCandidatesList = [];
    const candidateErrors = {};

    formData.candidates.forEach((candidate, index) => {
      const candidateValidationErrors = validateCandidate(candidate);

      if (Object.keys(candidateValidationErrors).length > 0) {
        candidateErrors[index] = candidateValidationErrors;
        invalidCandidatesList.push(candidate);
      }
    });

    if (Object.keys(candidateErrors).length > 0) {
      errors.candidates = candidateErrors;
    }

    // Update invalid candidates list
    setInvalidCandidates(invalidCandidatesList);

    // Update form errors
    setFormErrors(errors);

    // Form is valid if no errors exist
    return Object.keys(errors.shared).length === 0
        && Object.keys(errors.candidates).length === 0
        && !errors.message;
  }, [formData, validateCandidate]);

  // Handler for candidate field changes
  const handleCandidateChange = useCallback((index, field, value) => {
    setFormData(prev => {
      const newCandidates = [...prev.candidates];
      newCandidates[index] = {
        ...newCandidates[index],
        [field]: value
      };
      return {
        ...prev,
        candidates: newCandidates
      };
    });
  }, []);

  // Handler for shared field changes
  const handleSharedInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      shared: {
        ...prev.shared,
        [name]: value
      }
    }));
  }, []);

  // Handler for questions selection
  const handleQuestionChange = useCallback((event, newValue) => {
    if (newValue.length <= MAX_QUESTIONS) {
      setFormData(prev => ({
        ...prev,
        shared: {
          ...prev.shared,
          selectedQuestions: newValue.map(value => parseInt(value))
        }
      }));
    }
  }, []);

  // Handler for removing a candidate
  const handleRemoveCandidate = useCallback(async (candidateToRemove) => {
    if (!candidateToRemove || !candidateToRemove.ApplicationID) {
      console.error("Invalid candidate data:", candidateToRemove);
      return;
    }

    // Use a single setState call with functional updates to prevent race conditions
    const appId = candidateToRemove.ApplicationID;

    setFormData(prev => ({
      ...prev,
      candidates: prev.candidates.filter(c => c.applicantID !== appId),
      candidateStatuses: {
        ...prev.candidateStatuses,
        [appId]: undefined // Remove status for this candidate
      }
    }));

    setPendingCandidates(prev => prev.filter(c => c.ApplicationID !== appId));
    setCompletedCandidates(prev => prev.filter(c => c.ApplicationID !== appId));
    setInvalidCandidates(prev => prev.filter(c =>
        (c.ApplicationID || c.applicantID) !== appId
    ));
  }, []);

  // Function to process message for submission (replace newlines with separator character)
  const processMessageForSubmission = useCallback((message) => {
    if (!message) return message;
    // Using '¶' (pilcrow) as it's rarely used in normal text
    return message.replace(/\n/g, '¶');
  }, []);

  // Find invalid variables in message text
  const findInvalidVariables = useCallback((text) => {
    if (!text) return [];

    const variableRegex = /{[^}]+}/g;
    const matches = text.match(variableRegex) || [];
    const invalidVariables = [];

    matches.forEach(match => {
      // Check if variable is valid
      if (!Object.keys(VALID_VARIABLES).includes(match)) {
        const matchClean = match.toLowerCase().replace(/[{}\s]/g, '');
        let foundMatch = false;

        // Check if it matches any alternate forms
        for (const [validVar, alternates] of Object.entries(VALID_VARIABLES)) {
          if (alternates.includes(matchClean)) {
            invalidVariables.push({
              invalid: match,
              suggestion: validVar
            });
            foundMatch = true;
            break;
          }
        }

        if (!foundMatch) {
          invalidVariables.push({
            invalid: match,
            suggestion: 'Unknown variable'
          });
        }
      }
    });

    return invalidVariables;
  }, []);

  // Form submission handler with error handling
  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    // Validate form
    if (!validateForm()) {
      // If validation fails, scroll to first error
      const firstErrorElement = document.querySelector('.Mui-error');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsLoading(true);
    setApiError(null);

    // Convert time preferences to UTC
    const convertTimeToUTC = (timeRange) => {
      const [start, end] = timeRange.split(' to ');
      const today = moment().format('YYYY-MM-DD');
      const startUTC = moment.tz(`${today} ${start}`, formData.shared.userTimeZone).utc().format('HH:mm');
      const endUTC = moment.tz(`${today} ${end}`, formData.shared.userTimeZone).utc().format('HH:mm');
      return [startUTC, endUTC];
    };

    // Create an array to track API call promises
    const apiCalls = [];
    const successfulCandidates = [];
    const failedCandidates = [];

    // Process each candidate
    formData.candidates.forEach(candidate => {
      // Skip if candidate is already being processed
      if (formData.candidateStatuses[candidate.applicantID] === "pending" ||
          formData.candidateStatuses[candidate.applicantID] === "ready") {
        return;
      }

      // Prepare submission data
      const submissionData = {
        ...formData.shared,
        missedCallMessage: processMessageForSubmission(formData.shared.missedCallMessage),
        candidateName: candidate.candidateName,
        candidateNumber: candidate.candidateNumber,
        candidateEmail: candidate.candidateEmail,
        applicantID: candidate.applicantID,
      };

      // Add time preferences
      if (formData.shared.contactTimePreference === '8to12') {
        const [scheduleAfter, scheduleBefore] = convertTimeToUTC('08:00 to 12:00');
        submissionData.scheduleAfter = scheduleAfter;
        submissionData.scheduleBefore = scheduleBefore;
      } else if (formData.shared.contactTimePreference === '12to5') {
        const [scheduleAfter, scheduleBefore] = convertTimeToUTC('12:00 to 17:00');
        submissionData.scheduleAfter = scheduleAfter;
        submissionData.scheduleBefore = scheduleBefore;
      } else if (formData.shared.contactTimePreference === '5to8') {
        const [scheduleAfter, scheduleBefore] = convertTimeToUTC('17:00 to 20:00');
        submissionData.scheduleAfter = scheduleAfter;
        submissionData.scheduleBefore = scheduleBefore;
      } else if (formData.shared.contactTimePreference === '8to8') {
        const [scheduleAfter, scheduleBefore] = convertTimeToUTC('08:00 to 20:00');
        submissionData.scheduleAfter = scheduleAfter;
        submissionData.scheduleBefore = scheduleBefore;
      }

      // Add interview scheduling questions if needed
      if (formData.shared.scheduleInterview) {
        const schedulingQuestions = [5, 6]; // Questions for interview scheduling
        schedulingQuestions.forEach(q => {
          if (!submissionData.selectedQuestions.includes(q)) {
            submissionData.selectedQuestions.push(q);
          }
        });
      }

      // Make API call with retry logic
      const makeApiCall = async (retryCount = 0) => {
        try {
          const response = await axios.post(
              `${backendURL}/v1/prescreen`,
              submissionData,
              {
                headers: { 'Content-Type': 'application/json' },
                timeout: 10000 // 10-second timeout
              }
          );
          successfulCandidates.push(candidate.candidateName);
          return response;
        } catch (error) {
          // Retry logic for network errors or 5xx responses
          if (retryCount < 2 && (
              !error.response ||
              error.response.status >= 500 ||
              error.code === 'ECONNABORTED'
          )) {
            // Exponential backoff
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
            return makeApiCall(retryCount + 1);
          }

          // If retries exhausted or non-retryable error
          failedCandidates.push(candidate.candidateName);
          throw error;
        }
      };

      apiCalls.push(makeApiCall());
    });

    // Execute all API calls
    try {
      await Promise.allSettled(apiCalls);

      // Show success/failure messages
      if (successfulCandidates.length > 0) {
        if (successfulCandidates.length === 1) {
          message.success(`Prescreening request created for ${successfulCandidates[0]}`);
        } else {
          message.success(`Prescreening requests created for ${successfulCandidates.length} candidates`);
        }
      }

      if (failedCandidates.length > 0) {
        if (failedCandidates.length === 1) {
          message.error(`Failed to create prescreening request for ${failedCandidates[0]}`);
        } else {
          message.error(`Failed to create prescreening requests for ${failedCandidates.length} candidates`);
        }
      }

      // Close modal on success (if any successful submissions)
      if (successfulCandidates.length > 0) {
        // Immediately refresh prescreen status for submitted candidates
        if (onPrescreenSubmitted) {
          onPrescreenSubmitted();
        }
        handleClose();
      } else if (failedCandidates.length > 0) {
        setApiError("Failed to create prescreening requests. Please try again.");
      }
    } catch (error) {
      console.error("Error in prescreening API calls:", error);
      setApiError("An error occurred while submitting prescreening requests.");
    } finally {
      setIsLoading(false);
    }
  }, [formData, validateForm, backendURL, processMessageForSubmission, handleClose]);

  // Toggle for missed call message
  const handleToggleMissedCallMessage = useCallback((enabled) => {
    setFormData(prev => ({
      ...prev,
      shared: {
        ...prev.shared,
        sendMissedCallMessage: enabled,
        missedCallMessage: enabled ? DEFAULT_MESSAGE : ''
      }
    }));
  }, []);

  // Insert variable into missed call message
  const handleInsertVariable = useCallback((variable) => {
    const textField = document.querySelector('[name="missedCallMessage"]');
    if (!textField) return;

    const start = textField.selectionStart;
    const end = textField.selectionEnd;
    const currentValue = formData.shared.missedCallMessage;
    const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);

    setFormData(prev => ({
      ...prev,
      shared: {
        ...prev.shared,
        missedCallMessage: newValue
      }
    }));

    // Set focus back to the text field after state update
    setTimeout(() => {
      textField.focus();
      textField.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  }, [formData.shared.missedCallMessage]);

  // Calculate status colors and text for candidate items
  const getCandidateStatus = useCallback((candidate) => {
    const isPending = formData.candidateStatuses[candidate.applicantID] === "pending";
    const isCompleted = formData.candidateStatuses[candidate.applicantID] === "ready";
    const isInvalid = invalidCandidates.some(inv =>
        inv.applicantID === candidate.applicantID ||
        inv.ApplicationID === candidate.applicantID
    );

    let backgroundColor = 'inherit';
    let statusText = '';
    let statusColor = '';

    if (isInvalid) {
      backgroundColor = '#ffebee'; // Light red for invalid
      statusText = '(Invalid Information)';
      statusColor = '#d32f2f'; // Red
    } else if (isPending) {
      backgroundColor = '#fff4f4';
      statusText = '(Prescreening Pending)';
      statusColor = '#d32f2f'; // Red
    } else if (isCompleted) {
      backgroundColor = '#fff4f4';
      statusText = '(Prescreening Complete)';
      statusColor = '#1f96bb'; // Blue
    }

    return { backgroundColor, statusText, statusColor };
  }, [formData.candidateStatuses, invalidCandidates]);

  // Render a candidate accordion item
  const renderCandidateItem = useCallback((candidate, index) => {
    const { backgroundColor, statusText, statusColor } = getCandidateStatus(candidate);
    const candidateErrors = formErrors.candidates[index] || {};

    return (
        <Accordion
            key={index}
            expanded={expandedPanel === index}
            onChange={() => setExpandedPanel(expandedPanel === index ? false : index)}
            sx={{
              mb: 1,
              backgroundColor,
              position: 'relative',
            }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              {candidate.candidateName || `Candidate ${index + 1}`}
              {statusText && (
                  <Typography
                      component="span"
                      sx={{
                        color: statusColor,
                        ml: 2,
                        fontSize: '0.875rem',
                      }}
                  >
                    {statusText}
                  </Typography>
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Candidate Name"
                    value={candidate.candidateName}
                    onChange={(e) => handleCandidateChange(index, 'candidateName', e.target.value)}
                    error={!!candidateErrors.candidateName}
                    helperText={candidateErrors.candidateName || ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Phone Number"
                    value={candidate.candidateNumber}
                    onChange={(e) => handleCandidateChange(index, 'candidateNumber', e.target.value)}
                    error={!!candidateErrors.candidateNumber}
                    helperText={candidateErrors.candidateNumber || 'Include country code (e.g., +61 for Australia)'}
                    placeholder="+61 XXX XXX XXX"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Email"
                    value={candidate.candidateEmail}
                    onChange={(e) => handleCandidateChange(index, 'candidateEmail', e.target.value)}
                    error={!!candidateErrors.candidateEmail}
                    helperText={candidateErrors.candidateEmail || ''}
                />
              </Grid>
              {(statusText || formData.candidateStatuses[candidate.applicantID]) && (
                  <Grid item xs={12}>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<CloseIcon />}
                        onClick={() => {
                          const transformedCandidate = {
                            ApplicationID: candidate.ApplicationID || candidate.applicantID,
                            Name: candidate.Name || candidate.candidateName
                          };
                          handleRemoveCandidate(transformedCandidate);
                        }}
                        sx={{ mt: 1 }}
                    >
                      Remove from Selection
                    </Button>
                  </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
    );
  }, [expandedPanel, formErrors.candidates, formData.candidateStatuses, getCandidateStatus, handleCandidateChange, handleRemoveCandidate]);

  // Calculate header styling and counts
  const headerInfo = useMemo(() => {
    const hasInvalid = invalidCandidates.length > 0;
    const hasPending = pendingCandidates.length > 0;
    let backgroundColor = 'inherit';

    if (hasInvalid) {
      backgroundColor = '#fff4f4';
    } else if (hasPending) {
      backgroundColor = '#fff4f4';
    }

    return {
      backgroundColor,
      hasInvalid,
      hasPending,
      pendingCount: pendingCandidates.length,
      completedCount: completedCandidates.length,
      totalCount: formData.candidates.length
    };
  }, [invalidCandidates.length, pendingCandidates.length, completedCandidates.length, formData.candidates.length]);

  // Get the list of invalid variables in the message
  const invalidMessageVariables = useMemo(() => {
    if (!formData.shared.missedCallMessage) return [];
    return findInvalidVariables(formData.shared.missedCallMessage);
  }, [formData.shared.missedCallMessage, findInvalidVariables]);

  // Modal style
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    p: '30px',
    overflowY: 'auto',
  };

  // Only render when open
  if (!open) return null;

  return (
      <Modal
          open={open}
          onClose={handleClose}
          destroyOnClose={true}
          aria-labelledby="modal-title"
          keepMounted={false}
          disablePortal={false}
      >
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ color: '#303f9f', mb: 3 }}>
            Pre-Screening Form
          </Typography>

          {/* Loading state */}
          {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography>Loading...</Typography>
              </Box>
          )}

          {/* API Error */}
          {apiError && (
              <Alert
                  severity="error"
                  sx={{ mb: 3 }}
                  onClose={() => setApiError(null)}
              >
                <AlertTitle>Error</AlertTitle>
                {apiError}
              </Alert>
          )}

          {/* Pending/Completed Candidates Alert */}
          {(headerInfo.pendingCount > 0 || headerInfo.completedCount > 0) && (
              <Alert
                  severity="warning"
                  sx={{ mb: 3 }}
                  action={
                    <Button
                        color="inherit"
                        size="small"
                        onClick={() => {
                          // Remove both pending and completed candidates
                          const pendingIds = pendingCandidates.map(c => c.ApplicationID);
                          const completedIds = completedCandidates.map(c => c.ApplicationID);
                          const idsToRemove = [...pendingIds, ...completedIds];

                          setFormData(prev => ({
                            ...prev,
                            candidates: prev.candidates.filter(candidate =>
                                !idsToRemove.includes(candidate.applicantID)
                            ),
                            candidateStatuses: Object.fromEntries(
                                Object.entries(prev.candidateStatuses)
                                    .filter(([id]) => !idsToRemove.includes(id))
                            )
                          }));

                          // Clear arrays
                          setPendingCandidates([]);
                          setCompletedCandidates([]);
                        }}
                    >
                      Remove All {headerInfo.pendingCount + headerInfo.completedCount} Candidate{headerInfo.pendingCount + headerInfo.completedCount > 1 ? 's' : ''}
                    </Button>
                  }
              >
                {[
                  headerInfo.pendingCount > 0 && `${headerInfo.pendingCount} candidate(s) already have pending prescreening`,
                  headerInfo.completedCount > 0 && `${headerInfo.completedCount} candidate(s) have completed prescreening`
                ].filter(Boolean).join(' and ')}
              </Alert>
          )}

          {/* Invalid Candidates Alert */}
          {headerInfo.hasInvalid && (
              <Alert
                  severity="error"
                  sx={{ mb: 3 }}
                  action={
                    <Button
                        color="inherit"
                        size="small"
                        onClick={() => {
                          const invalidIds = invalidCandidates.map(c => c.applicantID || c.ApplicationID);

                          setFormData(prev => ({
                            ...prev,
                            candidates: prev.candidates.filter(candidate =>
                                !invalidIds.includes(candidate.applicantID)
                            )
                          }));

                          setInvalidCandidates([]);
                          setFormErrors(prev => ({
                            ...prev,
                            candidates: {}
                          }));
                        }}
                    >
                      Remove Invalid Candidates
                    </Button>
                  }
              >
                {invalidCandidates.length} candidate(s) have missing or invalid required fields (name, email, or phone number)
              </Alert>
          )}

          {/* Job Title Field */}
          <TextField
              fullWidth
              label="Job Title"
              value={formData.shared.jobTitle}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                shared: {
                  ...prev.shared,
                  jobTitle: e.target.value
                }
              }))}
              sx={{ mb: 3 }}
              disabled={isLoading}
          />

          {/* Main Candidates Accordion */}
          <Accordion
              expanded={expandedMainPanel}
              onChange={() => setExpandedMainPanel(!expandedMainPanel)}
              sx={{ mb: 3, backgroundColor: headerInfo.backgroundColor }}
              disabled={isLoading}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>
                Candidates ({headerInfo.totalCount})
                {headerInfo.pendingCount > 0 && (
                    <Typography
                        component="span"
                        sx={{
                          color: '#d32f2f',
                          ml: 2,
                          fontSize: '0.875rem'
                        }}
                    >
                      ({headerInfo.pendingCount} Pending)
                    </Typography>
                )}
                {headerInfo.completedCount > 0 && (
                    <Typography
                        component="span"
                        sx={{
                          color: '#1f96bb',
                          ml: 2,
                          fontSize: '0.875rem'
                        }}
                    >
                      ({headerInfo.completedCount} Completed)
                    </Typography>
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {formData.candidates.map((candidate, index) => renderCandidateItem(candidate, index))}
              {formData.candidates.length === 0 && (
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No candidates available
                  </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Shared Form Fields */}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Contact Method */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#303f9f' }}>
                  Contact Method *
                </Typography>
                <RadioGroup
                    row
                    name="contactMethod"
                    value={formData.shared.contactMethod}
                    onChange={handleSharedInputChange}
                    required
                >
                  <FormControlLabel
                      value="phone"
                      control={<Radio />}
                      label="Phone/Voice"
                      disabled={isLoading}
                  />
                </RadioGroup>
                {formErrors.shared.contactMethod && (
                    <Typography color="error" variant="caption">
                      {formErrors.shared.contactMethod}
                    </Typography>
                )}
              </Grid>

              {/* Questions Selection */}
              <Grid item xs={12}>
                <Autocomplete
                    multiple
                    options={Object.keys(QUESTION_MAPPING)}
                    getOptionLabel={(option) => QUESTION_MAPPING[option]}
                    value={formData.shared.selectedQuestions.map(id => id.toString())}
                    onChange={handleQuestionChange}
                    disabled={isLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={`Select Questions (max ${MAX_QUESTIONS}) *`}
                            error={!!formErrors.shared.selectedQuestions}
                            helperText={formErrors.shared.selectedQuestions}
                        />
                    )}
                />
              </Grid>

              {/* Contact Time Preference */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#303f9f' }}>
                  Preferred Contact Time *
                </Typography>
                <RadioGroup
                    row
                    name="contactTimePreference"
                    value={formData.shared.contactTimePreference}
                    onChange={handleSharedInputChange}
                    disabled={isLoading}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      '& .MuiFormControlLabel-root': {
                        marginBottom: 1
                      }
                    }}
                >
                  <FormControlLabel
                      value="8to12"
                      control={<Radio />}
                      label="8am - 12pm"
                  />
                  <FormControlLabel
                      value="12to5"
                      control={<Radio />}
                      label="12pm - 5pm"
                  />
                  <FormControlLabel
                      value="5to8"
                      control={<Radio />}
                      label="5pm - 8pm"
                  />
                  <FormControlLabel
                      value="8to8"
                      control={<Radio />}
                      label="8am - 8pm"
                  />
                </RadioGroup>
                {formErrors.shared.contactTimePreference && (
                    <Typography color="error" variant="caption">
                      {formErrors.shared.contactTimePreference}
                    </Typography>
                )}
              </Grid>

              {/* Time Zone */}
              <Grid item xs={12}>
                <Autocomplete
                    options={moment.tz.names()}
                    value={formData.shared.userTimeZone}
                    onChange={(event, newValue) =>
                        setFormData(prev => ({
                          ...prev,
                          shared: {
                            ...prev.shared,
                            userTimeZone: newValue
                          }
                        }))
                    }
                    disabled={isLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Your Time Zone *"
                            required
                            error={!!formErrors.shared.userTimeZone}
                            helperText={formErrors.shared.userTimeZone}
                        />
                    )}
                />
              </Grid>

              {/* Retry Count */}
              <Grid item xs={12}>
                <TextField
                    label="# Times To Retry"
                    type="number"
                    name="retryCount"
                    value={formData.shared.retryCount}
                    onChange={handleSharedInputChange}
                    required
                    error={!!formErrors.shared.retryCount}
                    helperText={formErrors.shared.retryCount || 'Enter a value between 0 and 5'}
                    disabled={isLoading}
                    inputProps={{
                      min: 0,
                      max: 5
                    }}
                />
              </Grid>

              {/* Send Email Notification Switch */}
              <Grid item xs={12}>
                <FormControlLabel
                    control={
                      <Switch
                          checked={formData.shared.sendEmail}
                          onChange={(e) =>
                              setFormData(prev => ({
                                ...prev,
                                shared: {
                                  ...prev.shared,
                                  sendEmail: e.target.checked
                                }
                              }))
                          }
                          disabled={isLoading}
                      />
                    }
                    label={
                      <Tooltip 
                        title={formData.shared.scheduleInterview ? 
                          "Automatically synced with Schedule Interview setting" : 
                          "Send prescreening results via email"
                        }
                        placement="top"
                      >
                        <span>Send Results via Email</span>
                      </Tooltip>
                    }
                />
              </Grid>

              {/* Schedule Interview Switch */}
              <Grid item xs={12}>
                <FormControlLabel
                    control={
                      <Switch
                          checked={formData.shared.scheduleInterview}
                          onChange={(e) =>
                              setFormData(prev => ({
                                ...prev,
                                shared: {
                                  ...prev.shared,
                                  scheduleInterview: e.target.checked,
                                  // Automatically sync email send with schedule interview
                                  sendEmail: e.target.checked
                                }
                              }))
                          }
                          disabled={isLoading}
                      />
                    }
                    label="Schedule Interview"
                />
              </Grid>

            
              {/* Interview Duration (conditional) */}
              {formData.shared.scheduleInterview && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#303f9f' }}>
                      Interview Duration *
                    </Typography>
                    <RadioGroup
                        row
                        name="interviewSlot"
                        value={formData.shared.interviewSlot}
                        onChange={handleSharedInputChange}
                        disabled={isLoading}
                    >
                      <FormControlLabel value="15min" control={<Radio />} label="15 min" />
                      <FormControlLabel value="30min" control={<Radio />} label="30 min" />
                      <FormControlLabel value="60min" control={<Radio />} label="60 min" />
                    </RadioGroup>
                    {formErrors.shared.interviewSlot && (
                        <Typography color="error" variant="caption">
                          {formErrors.shared.interviewSlot}
                        </Typography>
                    )}
                  </Grid>
              )}

              {/* Missed Call Message Section */}
              <Grid item xs={12}>
                <FormControlLabel
                    control={
                      <Switch
                          checked={formData.shared.sendMissedCallMessage}
                          onChange={(e) => handleToggleMissedCallMessage(e.target.checked)}
                          disabled={isLoading}
                      />
                    }
                    label="Send Missed Call SMS"
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 1 }}>
                  This message will be sent if the candidate does not respond to the final phone call attempt.
                </Typography>

                {formData.shared.sendMissedCallMessage && (
                    <>
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Available Variables (click to insert):
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {Object.keys(VALID_VARIABLES).map((variable) => (
                              <Button
                                  key={variable}
                                  variant="outlined"
                                  size="small"
                                  sx={{ textTransform: 'none' }}
                                  onClick={() => handleInsertVariable(variable)}
                                  disabled={isLoading}
                              >
                                {variable}
                              </Button>
                          ))}
                        </Box>
                      </Box>

                      <TextField
                          fullWidth
                          multiline
                          rows={4}
                          name="missedCallMessage"
                          value={formData.shared.missedCallMessage}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              shared: {
                                ...prev.shared,
                                missedCallMessage: e.target.value
                              }
                            }));
                          }}
                          error={!!formErrors.message || invalidMessageVariables.length > 0}
                          FormHelperTextProps={{
                            component: 'div'
                          }}
                          helperText={
                            <div>
                              {formErrors.message && (
                                  <Typography color="error" component="p" variant="caption">
                                    {formErrors.message}
                                  </Typography>
                              )}
                              {invalidMessageVariables.map(({invalid, suggestion}, index) => (
                                  <Typography key={index} color="error" component="p" variant="caption" sx={{ mt: 0.5 }}>
                                    {suggestion === 'Unknown variable'
                                        ? `"${invalid}" is not a valid variable`
                                        : `Replace "${invalid}" with "${suggestion}"`}
                                  </Typography>
                              ))}
                              <Typography color="textSecondary" component="p" variant="caption">
                                {`${formData.shared.missedCallMessage.length}/${MAX_MESSAGE_LENGTH} characters`}
                              </Typography>
                            </div>
                          }
                          placeholder="Enter your message here. Use the variable buttons above to insert placeholders."
                          disabled={isLoading}
                      />
                    </>
                )}
              </Grid>

              {/* Submit Buttons */}
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Button
                    onClick={handleClose}
                    disabled={isLoading}
                    sx={{
                      mr: 2,
                      backgroundColor: '#ccc',
                      color: '#333',
                      '&:hover': {
                        backgroundColor: '#b3b3b3',
                      }
                    }}
                >
                  Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading || formData.candidates.length === 0}
                    sx={{
                      backgroundColor: '#d32f2f',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#b71c1c',
                      }
                    }}
                >
                  {isLoading ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                        Processing...
                      </>
                  ) : 'Pre-Screen'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Modal>
  );
}

export default PreScreeningModal;